import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";

import { type SignatureDeviceFailure } from "./providers/signature-provider.type";
import {
  SIGWEB_TLS_BASE_URL,
  describeSigWebFailure,
  toDeviceFailure
} from "./providers/sigweb.client";
import { topazSignatureProvider } from "./providers/topaz.provider";
import {
  type SignatureEntryPoint,
  trackSignaturePadUsed
} from "./signature-telemetry";

const Root = styled(Box)(({ theme }) => ({
  "&.MuiBox-root": {
    borderRadius: "4px",
    border: `1px solid ${theme.palette.common.black}`
  }
}));

/**
 * Direct link to Topaz's SigWeb installer.
 *
 * Deliberately the binary rather than the product page: staff hitting this
 * button have already been told what is wrong and just need the installer, and
 * the landing page buries it behind a product tour. Kept `target="_blank"` so a
 * vendor-side move (a deep link like this rots more easily than a landing page)
 * surfaces in a new tab instead of navigating the signing dialog away.
 */
export const SIGWEB_DOWNLOAD_URL =
  "https://www.topazsystems.com/software/sigweb.exe";

/**
 * Support's setup walkthrough. Staff who reach the not-found state have a pad
 * in front of them and a driver that is missing, stopped or expired — this is
 * the page that gets them from there to a working pad without a support ticket,
 * which is the whole point of the state (ENG-1900).
 */
export const SIGNATURE_PAD_HELP_ARTICLE_URL =
  "https://help.suno.tech/en/articles/15656089-setting-up-your-signature-pad";

/**
 * Opening this in a tab shows the browser's own verdict — a certificate
 * warning versus "connection refused" — which is the one diagnostic staff can
 * relay to support that actually distinguishes an expired cert from a service
 * that is not running.
 */
export const SIGWEB_STATUS_URL = `${SIGWEB_TLS_BASE_URL}SigWebVersion`;

/**
 * What a practice can actually buy today, in the copy staff see.
 *
 * The XP-Pen belongs here even though it never touches SigWeb: it is the pad
 * Support has always recommended, and its owners land on exactly this screen
 * looking for it. Telling them it works in the Draw tab is the difference
 * between "Suno doesn't support my pad" and one click.
 */
export const SUPPORTED_SIGNATURE_PADS =
  "Supported today: Topaz T-LBK460 and T-S460 (SigLite 1x5). An XP-Pen Star G430S draws in the Draw tab.";

/** One sentence, in the order the fix happens. */
export const SIGNATURE_PAD_NOT_FOUND_CAUSE =
  "Plug in a Topaz pad and install the SigWeb driver — about two minutes, Windows only.";

/**
 * Not-found copy per cause. The remedy differs, and showing the wrong one is
 * the failure this exists to prevent: a receptionist with SigWeb running and a
 * USB cable out must not be sent to reinstall SigWeb (the 6889 case), while
 * one with nothing installed must not be told to check a cable.
 */
const NOT_FOUND_COPY: Record<
  SignatureDeviceFailure,
  { title: string; cause: string; hint?: string; offerInstall: boolean }
> = {
  "no-device": {
    title: "Signature pad not responding",
    cause:
      "SigWeb is running on this computer, but no pad is answering. Check the pad's USB cable, then check again.",
    offerInstall: false
  },
  timeout: {
    title: "Signature pad not responding",
    cause:
      "SigWeb did not answer in time. Make sure the pad is plugged in and SigWeb is running, then check again.",
    offerInstall: true
  },
  "service-unreachable": {
    title: "No signature pad found on this computer",
    cause: SIGNATURE_PAD_NOT_FOUND_CAUSE,
    // Chrome gates a public page's requests to loopback behind a per-site
    // "local network" permission. Deny or dismiss it once and every probe
    // from that site fails exactly like an uninstalled SigWeb — which is how
    // the same machine signed on one deploy preview and reported "not found"
    // on the next (RUM, 2026-09-03). Nothing in code can tell the two apart,
    // so the copy has to name it.
    hint: "If Chrome asks whether this site may access devices on your local network, choose Allow, then check again.",
    offerInstall: true
  },
  error: {
    title: "Signature pad not responding",
    cause:
      "SigWeb returned an error. Check again; if it keeps happening, reinstall SigWeb.",
    offerInstall: true
  }
};

/**
 * How often we ask the pad for new ink. The vendor's own mirror polls at
 * 100 ms; 250 ms on loopback is still well under the reaction time of someone
 * watching the screen while the pad is signed, and the image is only fetched
 * when the point count actually changed.
 */
const INK_POLL_INTERVAL_MS = 250;

type TopazSignatureDeskProps = {
  width: number;
  height: number;
  beforeButtonSlot?: ReactNode;
  isSubmitButton?: boolean;
  submitButtonText?: string;
  isLoading?: boolean;
  /** Which signing workflow this desk was opened from; reported to RUM. */
  entryPoint?: SignatureEntryPoint;
  /**
   * Called whenever SigWeb answers a detection here, including one triggered
   * by "Check again". Lets the container record that this workstation really
   * does have a pad, so the tab is offered on its own next time.
   */
  onDeviceDetected?: (info: {
    version: string | null;
    model: string | null;
  }) => void;
  onCapture: (file: File) => void;
};

type DeviceState = "detecting" | "ready" | "unavailable";

interface DeviceInfo {
  version: string | null;
  model: string | null;
}

type StatusTone = "neutral" | "success" | "warning";

/** The one-line device status above the desk: a coloured dot and a label. */
function StatusLine({
  tone,
  children
}: {
  tone: StatusTone;
  children: ReactNode;
}) {
  const dotColor = {
    neutral: "text.disabled",
    success: "success.main",
    warning: "warning.main"
  }[tone];

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{ mb: 1 }}
      data-testid="topaz-status"
      data-tone={tone}
    >
      <Box
        component="span"
        aria-hidden
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: dotColor,
          flexShrink: 0
        }}
      />
      <Typography variant="caption" color="text.secondary">
        {children}
      </Typography>
    </Stack>
  );
}

function describeDevice({ model, version }: DeviceInfo): string {
  const name = model ? `Topaz ${model}` : "Signature pad";
  return version
    ? `${name} connected · SigWeb ${version}`
    : `${name} connected`;
}

export default function TopazSignatureDesk({
  width,
  height,
  isSubmitButton,
  isLoading,
  submitButtonText = "Confirm Signature",
  beforeButtonSlot,
  entryPoint,
  onDeviceDetected,
  onCapture
}: TopazSignatureDeskProps) {
  const [deviceState, setDeviceState] = useState<DeviceState>("detecting");
  const [failure, setFailure] = useState<SignatureDeviceFailure | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hasInk, setHasInk] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>({
    version: null,
    model: null
  });

  // The dialog can close mid-request; never setState on an unmounted tree.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Latest-value refs. Neither prop may restart detection: restarting re-arms
  // the pad, which WIPES whatever the signer has drawn so far. A parent that
  // re-renders with a fresh inline callback must cost nothing here.
  const onDeviceDetectedRef = useRef(onDeviceDetected);
  onDeviceDetectedRef.current = onDeviceDetected;
  const entryPointRef = useRef(entryPoint);
  entryPointRef.current = entryPoint;

  // The live preview is an object URL over the bytes SigWeb served. Exactly
  // one is alive at a time; the previous one is revoked as the next arrives.
  const previewUrlRef = useRef<string | null>(null);
  const showPreview = useCallback((blob: Blob | null) => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    if (blob) {
      previewUrlRef.current = URL.createObjectURL(blob);
    }
    if (isMountedRef.current) setPreviewUrl(previewUrlRef.current);
  }, []);
  useEffect(() => () => showPreview(null), [showPreview]);

  // Detect the service, then arm the pad. Re-runs on "Check again".
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setDeviceState("detecting");
      setFailure(null);
      setErrorMessage(null);
      setHasInk(false);
      showPreview(null);

      const status = await topazSignatureProvider.isAvailable();
      if (cancelled || !isMountedRef.current) return;

      const info: DeviceInfo = {
        version: status.version ?? null,
        model: status.model ?? null
      };
      setDeviceInfo(info);

      const reportNotFound = () =>
        trackSignaturePadUsed({
          method: "topaz",
          outcome: "not_found",
          version: info.version,
          model: info.model,
          entryPoint: entryPointRef.current
        });

      if (!status.available) {
        setDeviceState("unavailable");
        setFailure(status.failure ?? "service-unreachable");
        setErrorMessage(status.error ?? describeSigWebFailure(null));
        // Fired per failed detection rather than per render, so the count in
        // RUM is "times staff hit a dead pad" — the number the rollout is
        // judged on — and a re-render or a poll cannot inflate it.
        reportNotFound();
        return;
      }

      // Report BEFORE arming: the service answering is what makes this a pad
      // workstation. Arming can still fail on a busy tablet, and that must not
      // cost the workstation its tab — the not-found state is where it recovers.
      onDeviceDetectedRef.current?.(info);

      try {
        await topazSignatureProvider.start({ width, height });
        if (cancelled || !isMountedRef.current) return;
        setDeviceState("ready");
      } catch (error) {
        if (cancelled || !isMountedRef.current) return;
        setDeviceState("unavailable");
        setFailure(toDeviceFailure(error));
        setErrorMessage(describeSigWebFailure(error));
        // A service that answers but will not arm is a dead pad to the person
        // holding it, so it counts the same way.
        reportNotFound();
      }
    })();

    return () => {
      cancelled = true;
      // Release the pad whenever we leave this desk (unmount, retry, or a
      // switch back to the canvas). Best-effort by contract — never throws.
      void topazSignatureProvider.stop();
    };
  }, [width, height, attempt, showPreview]);

  // Mirror the ink as it is drawn. The point count is the cheap question; the
  // image is fetched only when the answer changed, which is the vendor's own
  // strategy. Poll failures are transient by nature (a pad briefly busy
  // mid-stroke), so they are swallowed rather than tearing down a working
  // session — Confirm re-validates authoritatively anyway.
  useEffect(() => {
    if (deviceState !== "ready") return undefined;

    let cancelled = false;
    let inFlight = false;
    let lastPoints = 0;

    const tick = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const points = await topazSignatureProvider.pointCount();
        if (cancelled || points === lastPoints) return;
        lastPoints = points;
        setHasInk(points > 0);

        const blob = points > 0 ? await topazSignatureProvider.preview() : null;
        if (cancelled) return;
        showPreview(blob);
      } catch {
        // Ignored on purpose — see comment above.
      } finally {
        inFlight = false;
      }
    };

    const timer = setInterval(() => void tick(), INK_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [deviceState, showPreview]);

  const handleClear = useCallback(async () => {
    setErrorMessage(null);
    try {
      await topazSignatureProvider.clear();
      if (isMountedRef.current) {
        setHasInk(false);
        showPreview(null);
      }
    } catch (error) {
      if (isMountedRef.current) setErrorMessage(describeSigWebFailure(error));
    }
  }, [showPreview]);

  const handleCapture = useCallback(async () => {
    setIsBusy(true);
    setErrorMessage(null);
    try {
      const file = await topazSignatureProvider.capture();
      if (!isMountedRef.current) return;
      trackSignaturePadUsed({
        method: "topaz",
        outcome: "confirmed",
        version: deviceInfo.version,
        model: deviceInfo.model,
        entryPoint: entryPointRef.current
      });
      onCapture(file);
    } catch (error) {
      if (isMountedRef.current) setErrorMessage(describeSigWebFailure(error));
    } finally {
      if (isMountedRef.current) setIsBusy(false);
    }
  }, [onCapture, deviceInfo]);

  const notFound = NOT_FOUND_COPY[failure ?? "service-unreachable"];
  // For a missing pad the client's message IS the cause sentence; repeating
  // it as a diagnostic would say the same thing twice.
  const showDiagnostic = failure !== "no-device" && Boolean(errorMessage);

  return (
    <>
      {deviceState === "detecting" && (
        <StatusLine tone="neutral">Looking for a signature pad…</StatusLine>
      )}
      {deviceState === "ready" && (
        <StatusLine tone="success">{describeDevice(deviceInfo)}</StatusLine>
      )}
      {deviceState === "unavailable" && (
        <StatusLine tone="warning">{notFound.title}</StatusLine>
      )}

      <Root>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          sx={{
            width,
            // The not-found state carries copy, links, a button and the
            // supported-pad list; on the 400×200 desk that exceeds the frame.
            // Let that one state grow rather than scroll inside a border.
            height: deviceState === "unavailable" ? "auto" : height,
            minHeight: height,
            p: 2,
            boxSizing: "border-box"
          }}
        >
          {deviceState === "detecting" && (
            <>
              <CircularProgress size={28} />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Looking for the Topaz signature pad&hellip;
              </Typography>
            </>
          )}

          {deviceState === "unavailable" && (
            <>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                data-testid="topaz-unavailable-title"
              >
                {notFound.title}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {notFound.cause}
              </Typography>
              {notFound.hint && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {notFound.hint}
                </Typography>
              )}
              {/* The plain cause above is what staff act on; this is the
                  transport diagnostic that separates "not installed" from
                  "certificate expired" (they expire yearly), so it stays —
                  one step down in emphasis rather than leading. */}
              {showDiagnostic && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  // The diagnostic names a URL, which is one unbreakable token
                  // and otherwise spills out of the 400px desk.
                  sx={{ mt: 1, overflowWrap: "anywhere" }}
                >
                  {errorMessage}
                </Typography>
              )}
              <Stack
                direction="row"
                spacing={2}
                justifyContent="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 2 }}
              >
                <Link
                  variant="body2"
                  underline="hover"
                  href={SIGNATURE_PAD_HELP_ARTICLE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Set up your signature pad
                </Link>
                {notFound.offerInstall && (
                  <Link
                    variant="body2"
                    underline="hover"
                    href={SIGWEB_DOWNLOAD_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Install SigWeb
                  </Link>
                )}
                {failure === "service-unreachable" && (
                  <Link
                    variant="body2"
                    underline="hover"
                    href={SIGWEB_STATUS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Check SigWeb status
                  </Link>
                )}
              </Stack>
              <Button
                size="small"
                variant="contained"
                sx={{ mt: 2 }}
                onClick={() => setAttempt((value) => value + 1)}
              >
                Check again
              </Button>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 2 }}
              >
                {SUPPORTED_SIGNATURE_PADS}
              </Typography>
            </>
          )}

          {deviceState === "ready" &&
            (previewUrl ? (
              <Box
                component="img"
                src={previewUrl}
                alt="Signature in progress"
                data-testid="topaz-ink-preview"
                sx={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain"
                }}
              />
            ) : (
              <>
                <Typography variant="subtitle1" fontWeight={700}>
                  Sign on the Topaz pad
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Hand the pad to the signer. Strokes appear here as they are
                  drawn.
                </Typography>
              </>
            ))}
        </Box>
      </Root>

      {Boolean(errorMessage) && deviceState === "ready" && (
        <Alert severity="error" sx={{ mt: 2 }} data-testid="topaz-error">
          {errorMessage}
        </Alert>
      )}

      <Stack rowGap={2} sx={{ mt: 2 }}>
        {beforeButtonSlot}
        <Stack direction="row" columnGap={2}>
          <Button
            fullWidth
            variant="outlined"
            disabled={deviceState !== "ready" || isBusy}
            onClick={handleClear}
          >
            Clear
          </Button>
          {/* Confirm waits for ink, like the Type desk waits for a name: an
              enabled button over an empty pad invites a click that can only
              fail. Capture still re-checks the pad, so a stale poll cannot
              let an empty signature through. */}
          <LoadingButton
            fullWidth
            type={isSubmitButton ? "submit" : undefined}
            variant="contained"
            loading={isBusy || isLoading}
            disabled={deviceState !== "ready" || !hasInk}
            onClick={handleCapture}
          >
            {submitButtonText}
          </LoadingButton>
        </Stack>
      </Stack>
    </>
  );
}
